/*
 * Surface translation — throwaway summer stopgap.
 *
 * Machine-translates the already-rendered page into a "surface" language
 * (DE/IT/FR/SL/PL/CS/NL) on top of the real hr/en render. Base locales hr/en are
 * real routes and are NOT handled here. See docs/superpowers/specs/2026-08-05-*.
 *
 * Why a TreeWalker + MutationObserver (opt-out) instead of opt-in `data-i18n`
 * tags: most visible text has no static template to annotate. Service titles,
 * descriptions, categories and regions come from the DB; calendar month/day
 * names are generated by Vue; the Google-reviews and ez-booker widgets inject
 * text after load. The static UI copy that *could* be tagged is already keyed
 * via __()/Vue translate(). So we translate everything the walker sees, exclude
 * the few things we must never touch (see SKIP_* below), and re-run on DOM
 * mutations to catch Vue/Inertia/third-party re-renders.
 *
 * Shape: collect text nodes + placeholders -> dedupe -> POST /translate in
 * chunks -> write back in place (originals stashed in WeakMaps so we never feed
 * our own output back as source). Exports: setLanguage, clear, init.
 */

const COOKIE = "surface_lang";

/* ── What we never translate ──────────────────────────────────────────────
 * A text node is skipped if it is empty, a pure number/price/email/phone, or
 * lives under one of these ancestors. <option> text IS translated (display
 * only) so <select> labels localize — option values are attributes we never
 * touch. input/textarea/contenteditable stay skipped so we never alter
 * user-entered or submitted values. [data-surface-lang]/[data-real-locale] are
 * the switcher controls; their labels are language codes (HR, EN, DE…). */
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "CODE", "PRE", "TEXTAREA", "NOSCRIPT", "SVG"]);
const SKIP_SELECTOR = '[translate="no"], .notranslate, input, textarea, [contenteditable], [data-surface-lang], [data-real-locale]';
// Placeholders live ON inputs, so there we only honour explicit opt-out zones.
const OPT_OUT_SELECTOR = '[translate="no"], .notranslate';
const SKIP_TEXT_PATTERNS = [
  /^[\s\d.,:%€$£+\-/()]+$/,   // pure numbers / prices / symbols
  /^\S+@\S+\.\S+$/,           // emails
  /^[\s\d+()/-]{6,}$/,        // phone numbers
];

/* ── Language-switcher UI we keep in sync by hand ──────────────────────────
 * The server-rendered switcher only knows the base locale (hr/en), so after a
 * surface language is picked we update the flag/code ourselves. These selectors
 * mirror the Blade switcher partials (partials/navbar.blade.php,
 * partials/connect-navbar.blade.php, connect/partials/navbar.blade.php,
 * partials/footer.blade.php). */
const SWITCHER_LABEL = ".custom-dropdown-label";
const SWITCHER_LINK = ".sidebar-switcher-link";
// Footer switcher marks its active language with the `tw-font-bold` utility
// (server-set for hr/en); we move that bold onto the active surface link.
const FOOTER_LINK = ".footer-switcher-link";

// Max strings per /translate request — a page with a large <select> (e.g. the
// ~195-option country list on the booking form) would otherwise exceed the
// endpoint's per-request cap and get the whole batch rejected.
const CHUNK = 100;

const originals = new WeakMap();    // textNode -> original string
const lastWritten = new WeakMap();  // textNode -> value we last wrote (self-write guard)
const phOriginals = new WeakMap();  // input/textarea -> original placeholder string
let currentLang = null;
let observer = null;

/* ── Cookie helpers ───────────────────────────────────────────────────────*/
function getCookie(name) {
  const hit = document.cookie.split("; ").find((r) => r.startsWith(name + "="));
  return hit ? decodeURIComponent(hit.split("=")[1]) : null;
}
function setCookie(name, value) {
  document.cookie = `${name}=${encodeURIComponent(value)};path=/;max-age=${60 * 60 * 24 * 30};samesite=lax`;
}
function clearCookie(name) {
  document.cookie = `${name}=;path=/;max-age=0;samesite=lax`;
}

/* ── Skip logic ───────────────────────────────────────────────────────────*/
function matchesSkipPattern(text) {
  return SKIP_TEXT_PATTERNS.some((re) => re.test(text));
}

// True if `el` or any ancestor matches `selector` (or, if given, has a `tags`
// tag name). Shared by the text-node and placeholder collectors.
function hasBlockedAncestor(el, selector, tags = null) {
  while (el) {
    if (tags && tags.has(el.tagName)) return true;
    if (el.matches && el.matches(selector)) return true;
    el = el.parentElement;
  }
  return false;
}

function shouldSkip(node) {
  const text = node.nodeValue;
  if (!text || !text.trim()) return true;
  if (matchesSkipPattern(text.trim())) return true;
  return hasBlockedAncestor(node.parentElement, SKIP_SELECTOR, SKIP_TAGS);
}

/* ── Collection ───────────────────────────────────────────────────────────*/
function collectNodes(root) {
  if (!root || (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE)) {
    return [];
  }
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode: (n) => (shouldSkip(n) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  const nodes = [];
  let n;
  while ((n = walker.nextNode())) nodes.push(n);
  return nodes;
}

// Placeholders are attributes, not text nodes, so they're collected separately
// under the same number/email/phone and opt-out-ancestor rules.
function collectPlaceholders(root) {
  if (!root) return [];
  const candidates = [];
  if (root.matches && root.matches("input[placeholder], textarea[placeholder]")) {
    candidates.push(root);
  }
  if (root.querySelectorAll) {
    candidates.push(...root.querySelectorAll("input[placeholder], textarea[placeholder]"));
  }
  return candidates.filter((el) => {
    const ph = el.getAttribute("placeholder");
    if (!ph || !ph.trim()) return false;
    if (matchesSkipPattern(ph.trim())) return false;
    return !hasBlockedAncestor(el, OPT_OUT_SELECTOR);
  });
}

/* ── Translate: collect uniques → fetch → apply ───────────────────────────*/
// Stash each node/placeholder original into its WeakMap (so re-translation and
// the observer never feed our own output back as source) and return the unique
// set of trimmed source strings.
function collectUniqueStrings(nodes, phEls) {
  const unique = new Set();
  for (const node of nodes) {
    if (!originals.has(node)) originals.set(node, node.nodeValue);
    unique.add(originals.get(node).trim());
  }
  for (const el of phEls) {
    if (!phOriginals.has(el)) phOriginals.set(el, el.getAttribute("placeholder"));
    const orig = phOriginals.get(el);
    if (orig && orig.trim()) unique.add(orig.trim());
  }
  return [...unique].filter(Boolean);
}

// POST the strings to /translate in CHUNK-sized requests and merge the responses
// into one original→translation map. A failed HTTP or network chunk is skipped;
// the remaining chunks still apply.
async function fetchTranslations(texts, lang) {
  const source = (document.documentElement.lang || "auto").slice(0, 2);
  const map = {};
  for (let i = 0; i < texts.length; i += CHUNK) {
    try {
      const res = await fetch("/translate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-TOKEN": document.querySelector('meta[name="csrf-token"]')?.content || "",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify({ target: lang, source, texts: texts.slice(i, i + CHUNK) }),
      });
      if (!res.ok) continue;      // skip this chunk, keep the rest
      Object.assign(map, await res.json());
    } catch (e) {
      /* network failure for this chunk — leave those originals in place */
    }
  }
  return map;
}

// Write translations back in place, recording each write in `lastWritten` so the
// MutationObserver can tell our own edits from real content changes.
function applyTranslations(nodes, phEls, map) {
  for (const node of nodes) {
    const orig = originals.get(node);
    if (!orig) continue;
    const key = orig.trim();
    if (map[key]) {
      const out = orig.replace(key, map[key]);
      node.nodeValue = out;
      lastWritten.set(node, out);
    }
  }
  for (const el of phEls) {
    const orig = phOriginals.get(el);
    const key = orig && orig.trim();
    if (key && map[key]) el.setAttribute("placeholder", map[key]);
  }
}

async function translateNodes(nodes, phEls, lang) {
  nodes = nodes || [];
  phEls = phEls || [];
  if ((!nodes.length && !phEls.length) || !lang) return;

  const texts = collectUniqueStrings(nodes, phEls);
  if (!texts.length) return;

  const map = await fetchTranslations(texts, lang);
  if (!Object.keys(map).length) return;

  applyTranslations(nodes, phEls, map);
}

/* ── Live re-translation for Vue/Inertia/third-party re-renders ───────────*/
function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

function startObserver(lang) {
  if (observer) observer.disconnect();

  const pendingNodes = [];
  const pendingPh = [];
  const flush = debounce(() => {
    const nb = pendingNodes.splice(0, pendingNodes.length);
    const pb = pendingPh.splice(0, pendingPh.length);
    if (nb.length || pb.length) translateNodes(nb, pb, currentLang);
  }, 150);

  observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === "childList") {
        for (const node of m.addedNodes) {
          if (node.nodeType === Node.TEXT_NODE) {
            if (!shouldSkip(node)) pendingNodes.push(node);
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            pendingNodes.push(...collectNodes(node));
            pendingPh.push(...collectPlaceholders(node));
          }
        }
      } else if (m.type === "characterData") {
        const t = m.target;
        if (
          t.nodeType === Node.TEXT_NODE &&
          t.nodeValue !== lastWritten.get(t) &&   // ignore our own writes
          !shouldSkip(t)
        ) {
          originals.delete(t);                    // new source content -> restash
          pendingNodes.push(t);
        }
      }
    }
    if (pendingNodes.length || pendingPh.length) flush();
  });

  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
}

/* ── Switcher UI ──────────────────────────────────────────────────────────*/
// Reflect the chosen surface language in the switcher (flag + code). Without
// this the active button keeps showing HR/EN after a surface language is picked.
function reflectActiveLanguage(lang) {
  const code = lang.toUpperCase();
  const src = document.querySelector('[data-surface-lang="' + lang + '"] img')?.getAttribute("src");
  document.querySelectorAll(SWITCHER_LABEL).forEach((label) => {
    const img = label.querySelector("img");
    const span = label.querySelector("span");
    if (img && src) img.setAttribute("src", src);
    if (span) span.textContent = code;
  });
  document.querySelectorAll(SWITCHER_LINK + ".is-active").forEach((a) => a.classList.remove("is-active"));
  document
    .querySelectorAll(SWITCHER_LINK + '[data-surface-lang="' + lang + '"]')
    .forEach((a) => a.classList.add("is-active"));
  // Footer switcher: move the bold marker off hr/en onto the active surface link.
  document.querySelectorAll(FOOTER_LINK).forEach((a) => a.classList.remove("tw-font-bold"));
  document
    .querySelectorAll(FOOTER_LINK + '[data-surface-lang="' + lang + '"]')
    .forEach((a) => a.classList.add("tw-font-bold"));
}

/* ── Public API + bootstrap ───────────────────────────────────────────────*/
export async function setLanguage(lang) {
  currentLang = lang;
  setCookie(COOKIE, lang);
  try { localStorage.setItem(COOKIE, lang); } catch (e) { /* ignore */ }
  reflectActiveLanguage(lang);
  await translateNodes(collectNodes(document.body), collectPlaceholders(document.body), lang);
  startObserver(lang);
}

export function clear() {
  currentLang = null;
  clearCookie(COOKIE);
  try { localStorage.removeItem(COOKIE); } catch (e) { /* ignore */ }
  if (observer) observer.disconnect();
}

export function init() {
  document.addEventListener("click", (e) => {
    const surfaceBtn = e.target.closest ? e.target.closest("[data-surface-lang]") : null;
    if (surfaceBtn) {
      e.preventDefault();
      setLanguage(surfaceBtn.getAttribute("data-surface-lang"));
      return;
    }
    // Clicking a real-locale (hr/en) link clears surface state before navigating,
    // so the destination renders natively.
    if (e.target.closest && e.target.closest("[data-real-locale]")) {
      clear();
    }
  });

  const saved = getCookie(COOKIE);
  if (saved) setLanguage(saved);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
